import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, MapPin, Check, Star, Truck } from "lucide-react";
import Marquee from "react-fast-marquee";
import { waLink } from "@/lib/api";

const HERO_IMG = "/assets/fachada-10calle.jpg";

const TICKER = ["Lavadoras", "Refrigeradoras", "Estufas", "Secadoras", "Centros de Lavado", "Aire Acondicionado", "Congeladores"];
const TRUST = ["Garantía por escrito", "Servicio técnico por 1 año", "Financiamiento disponible", "Productos importados", "Atención personalizada"];
const LINES = ["Calidad, garantía", "y las mejores marcas", "para tu hogar."];

export default function Hero({ settings }) {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 100]);

  return (
    <header data-testid="hero-section" className="relative overflow-hidden bg-white pt-24 md:pt-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-muted-foreground"
              data-testid="hero-kicker"
            >
              <span className="inline-block h-2 w-2 bg-whatsapp" />
              Electrodomésticos Rivera — San Pedro Sula
            </motion.p>
            <h1
              data-testid="hero-headline"
              className="mt-6 text-4xl font-extrabold leading-[1.02] tracking-tighter text-primary sm:text-5xl lg:text-6xl"
            >
              {LINES.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}>
              <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="hero-trust-list">
                {TRUST.map((t, i) => (
                  <li key={t} className={`flex items-center gap-3 text-sm font-semibold text-primary ${i === TRUST.length - 1 && TRUST.length % 2 === 1 ? "sm:col-span-2" : ""}`}>
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center border border-primary/20">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <a
                  data-testid="hero-whatsapp-cta"
                  href={waLink(settings, "Hola Electrodomésticos Rivera, quiero cotizar un electrodoméstico.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-whatsapp px-8 text-base font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <MessageCircle className="h-5 w-5" /> Cotizar por WhatsApp
                </a>
                <a
                  data-testid="hero-locations-cta"
                  href="#ubicaciones"
                  className="inline-flex h-14 items-center justify-center gap-2 rounded-full border-2 border-primary px-8 text-base font-semibold text-primary transition-colors duration-300 hover:bg-primary hover:text-white"
                >
                  <MapPin className="h-5 w-5" /> Ver Ubicaciones
                </a>
              </div>
            </motion.div>
          </div>
          <div className="relative pb-10 lg:col-span-5 lg:pb-0">
            <motion.div
              style={{ y }}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -left-4 -top-4 h-full w-full border-2 border-primary/15" aria-hidden="true" />
              <img
                src={HERO_IMG}
                alt="Fachada de Electrodomésticos Rivera en San Pedro Sula — tienda de electrodomésticos importados"
                className="relative aspect-[4/3] w-full object-cover lg:aspect-[5/4]"
                loading="eager"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-5 -left-5 bg-primary px-5 py-4 text-white" data-testid="hero-rating-badge">
                <div className="flex items-center gap-1" aria-label="Cinco estrellas">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="mt-1 text-xs font-semibold">4.6 en Google · Clientes satisfechos en SPS</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      <div data-testid="shipping-banner" className="bg-primary">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-3 px-5 py-4 sm:flex-row sm:items-center md:px-8">
          <p className="flex items-center gap-3 text-sm font-semibold text-white">
            <Truck className="h-5 w-5 shrink-0" />
            Hacemos envíos a todo Honduras — tu electrodoméstico llega hasta donde estés.
          </p>
          <a
            data-testid="shipping-whatsapp-cta"
            href={waLink(settings, "Hola Electrodomésticos Rivera, quiero información sobre envíos a mi ciudad.")}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white underline decoration-whatsapp decoration-2 underline-offset-4 transition-colors duration-300 hover:text-whatsapp"
          >
            <MessageCircle className="h-4 w-4" /> Cotizar envío por WhatsApp
          </a>
        </div>
      </div>
      <div className="border-y border-border py-4" data-testid="hero-ticker">
        <Marquee speed={30} gradient={false}>
          {TICKER.map((t) => (
            <span key={t} className="mx-6 flex items-center gap-6 text-sm font-semibold uppercase tracking-[0.2em] text-primary/50">
              {t} <span className="text-whatsapp">•</span>
            </span>
          ))}
        </Marquee>
      </div>
    </header>
  );
}
